import {inject, NgModule} from '@angular/core';
import {Router, RouterModule, Routes, UrlTree} from '@angular/router';
import {AuthGuard, AuthUserService, ReversalAuthGuard} from 'app/services';
import {isMigrationAvailable, isAccountMigratable} from 'app/common';
import {AuthCallbackComponent} from './components/auth-callback/auth-callback.component';
import {NotFoundComponent} from 'app/containers/not-found/not-found.component';

/** true for legacy accounts that can be migrated: Studio Pro (0) and Studio Lite (1) */
function isMigratableUser(): boolean {
    return isAccountMigratable(inject(AuthUserService).user$.value);
}

/**
 * Root landing ("/"): a legacy Studio account (Pro or Lite) with migration available
 * (dev/local) lands straight on the full-page /migrate route; everyone else on /home.
 * Cancel on /migrate navigates to /home explicitly (which does NOT redirect), so there
 * is no bounce.
 */
function rootRedirect(): string {
    return isMigrationAvailable() && isMigratableUser() ? '/migrate' : '/home';
}

/**
 * Guards the /migrate route itself. Without this, the dev-only checks on the OTHER entry points
 * (root redirect, post-login navigate, the download-card button) are cosmetic: anyone could still
 * reach the page in production by typing /migrate, and "Migrate Now" runs a real, destructive
 * server migration. Bounces to /home when the feature is off or the account isn't legacy.
 */
function migrateGuard(): boolean | UrlTree {
    if (isMigrationAvailable() && isMigratableUser()) {
        return true;
    }
    return inject(Router).createUrlTree(['/home']);
}

/**
 * Every public URL here is unchanged - digitalsignage.com links straight to /getstarted and
 * /login, so the paths, their parameters and their guards are exactly what they were. What
 * changed is that the screens behind them are loaded as separate chunks, so that a visitor who
 * only needs one small page does not download the whole studio first.
 */
const routes: Routes = [
    {
        path: 'login',
        canActivate: [ReversalAuthGuard],
        loadChildren: () => import('./containers/start/start-routing.module').then(m => m.StartRoutingModule),
        data: {title: 'Login'}
    },
    // The equipment check-in app, matched BEFORE the shopping-cart route below so these two URLs
    // load a small dedicated module instead of ShoppingModule (which pulls SharedModule and with
    // it the whole studio). Same URLs, same ReversalAuthGuard, same title as when these were
    // children of ShoppingEntryComponent - these visitors never sign in to the studio itself.
    {
        path: 'shopping-cart/equipment',
        canActivate: [ReversalAuthGuard],
        loadChildren: () => import('./containers/user/equipped/equipped.module').then(m => m.EquippedModule),
        data: {title: 'Equipment'}
    },
    {
        path: 'shopping-cart/equipmentpol',
        canActivate: [ReversalAuthGuard],
        loadChildren: () => import('./containers/user/equipped/equipped.module').then(m => m.EquippedModule),
        data: {title: 'Equipment'}
    },
    {path: 'shopping-cart', children: [{path: '', loadChildren: () => import('./containers/cart/shopping.module').then(m => m.ShoppingModule)}], data: {title: 'Shopping cart'}},
    {path: 'monitor', children: [{path: '', loadChildren: () => import('./containers/monitor/monitor-module').then(m => m.MonitorModule)}], data: {title: 'Monitor'}},
    {path: 'getstarted', children: [{path: '', loadChildren: () => import('./containers/get-started/started.module').then(m => m.StartedModule)}], data: {title: 'Get started'}},
    {path: 'auth-callback', component: AuthCallbackComponent, data: {title: 'Authentication'}},
    // full-screen migration page (no MainComponent shell => no left nav / header), so a long
    // migration can't be interrupted by the surrounding app chrome. AuthGuard still protects it.
    {
        path: 'migrate',
        canActivate: [AuthGuard, migrateGuard],
        loadChildren: () => import('./components/migrate-account/migrate.module').then(m => m.MigrateModule),
        data: {title: 'Migrate'}
    },
    {path: '', redirectTo: rootRedirect, pathMatch: 'full'},
    {
        path: '',
        canActivate: [AuthGuard],
        loadChildren: () => import('./layouts/main/main.module').then(m => m.MainModule)
    },
    {path: 'not-found', component: NotFoundComponent, data: {title: 'Page not found (404)'}},
    {path: '**', component: NotFoundComponent, data: {title: 'Page not found (404)'}}
];

@NgModule({
    imports: [
        // RouterModule.forRoot(routes, { enableTracing: !environment.production })
        RouterModule.forRoot(routes, {enableTracing: false})
    ],
    exports: [RouterModule]
})
export class AppRoutingModule {
}
